import GlobalStyles from '@Guidelines/Theme/GlobalStyles';
import FacilitiesPage from '@UI/Pages/Agencies/Facilities';
import StaffMembersPage from '@UI/Pages/Agencies/StaffMembers';
import AiCostLimitsPage from '@UI/Pages/Administration/AiCostLimits';
import AiCostsPage from '@UI/Pages/Administration/AiCosts';
import AiEnginesPage from '@UI/Pages/Administration/AiEngines';
import AuditTrailPage from '@UI/Pages/Administration/AuditTrail';
import DocumentGroupEditPage from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit';
import DocumentGroupListPage from '@UI/Pages/Administration/DocumentGroups/DocumentGroupList';
import BeneficiaryTypeAccessPage from '@UI/Pages/Administration/SubjectTypes/BeneficiaryTypeAccess';
import SubjectTypeEditPage from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit';
import SubjectTypeListPage from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList';
import OrganizationsPage from '@UI/Pages/Administration/Organizations';
import RetentionPage from '@UI/Pages/Administration/Retention';
import RolesPage from '@UI/Pages/Administration/Roles';
import SsoPage from '@UI/Pages/Administration/Sso';
import UsersPage from '@UI/Pages/Administration/Users';
import AccountAiAssignmentsPage from '@UI/Pages/AiAssignments/AccountAiAssignments';
import FacilityAiAssignmentsPage from '@UI/Pages/AiAssignments/FacilityAiAssignments';
import OrganizationAiAssignmentsPage from '@UI/Pages/AiAssignments/OrganizationAiAssignments';
import FacilityDocumentTemplatesPage from '@UI/Pages/DocumentTemplates/FacilityDocumentTemplates';
import OrganizationDocumentTemplatesPage from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates';
import InvitationPage from '@UI/Pages/Authentication/Invitation';
import LoginPage from '@UI/Pages/Authentication/Login';
import PasswordForgottenPage from '@UI/Pages/Authentication/PasswordForgotten';
import PasswordResetPage from '@UI/Pages/Authentication/PasswordReset';
import SecondFactorPage from '@UI/Pages/Authentication/SecondFactor';
import SecondFactorEnrollmentPage from '@UI/Pages/Authentication/SecondFactorEnrollment';
import BeneficiariesListPage from '@UI/Pages/Beneficiaries/BeneficiaryList';
import BeneficiaryMembersPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryMembers';
import BeneficiaryContactsPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts';
import BeneficiarySummaryPage from '@UI/Pages/Beneficiaries/BeneficiaryDetails';
import SubjectLayout from '@UI/Pages/Beneficiaries/Shared/SubjectLayout';
import HomePage from '@UI/Pages/Home';
import WorkflowRunsPage from '@UI/Pages/WorkflowRuns';
import AuthenticatedUserProvider from '@UI/Providers/AuthenticatedUserProvider';
import InstallPromptProvider from '@UI/Providers/InstallPromptProvider';
import PermissionProvider from '@UI/Providers/PermissionProvider';
import SessionLossWatcher from '@UI/Providers/SessionLossWatcher';
import PortsProvider from '@UI/Providers/PortsProvider';
import type { AdministrationPath } from '@UI/Routing/administrationSections';
import ADMINISTRATION_SECTIONS from '@UI/Routing/administrationSections';
import type { FacilitySectionPath } from '@UI/Routing/facilitySections';
import FACILITY_SECTIONS from '@UI/Routing/facilitySections';
import FacilitySectionEntry from '@UI/Routing/FacilitySectionEntry';
import SectionLanding from '@UI/Routing/SectionLanding';
import { PermissionEnum } from '@Application/Security/Roles';
import RequireAnonymous from '@UI/Routing/RequireAnonymous';
import RequireChallenge from '@UI/Routing/RequireChallenge';
import RequireFacility from '@UI/Routing/RequireFacility';
import RequireBeneficiaryAccess from '@UI/Routing/RequireBeneficiaryAccess';
import RequirePermission from '@UI/Routing/RequirePermission';
import RequireSubjectType from '@UI/Routing/RequireSubjectType';
import renderSubjectDepositRoutes, { type ReadGuard } from '@UI/Routing/renderSubjectDepositRoutes';
import SubjectDetailsPage from '@UI/Pages/Subjects/SubjectDetails';
import SubjectMembersPage from '@UI/Pages/Subjects/SubjectDetails/Pages/Members';
import SubjectListPage from '@UI/Pages/Subjects/SubjectList';
import RequireSession from '@UI/Routing/RequireSession';
import rootStore from '@UI/Store';
import type * as React from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { MessageBrokerProvider } from '@verclam/message-broker';
import { StateManagerProvider } from '@verclam/synapse';
import { NotificationInitializer, NotificationProvider } from '@verclam/toast-v2';
import { UiKitProvider } from '@verclam/ui-kit';
import AppShell from '@Guidelines/Shell/AppShell';
import ApplicationOverlays from '@Guidelines/Shell/ApplicationOverlays';
import RootPage from '@Guidelines/Layout/RootPage';
import LoginRootPage from '@UI/Pages/Authentication/Shared/LoginRootPage';

/**
 * One entry per `AdministrationPath`: removing a section from `ADMINISTRATION_SECTIONS` without
 * removing its page here fails `ts-check`, and vice versa — the compiler ties the catalogue to the
 * routes it renders.
 */
const ADMINISTRATION_PAGES: Record<AdministrationPath, React.ComponentType> = {
	'/administration/users': UsersPage,
	'/administration/organizations': OrganizationsPage,
	'/administration/document-groups': DocumentGroupListPage,
	'/administration/subject-types': SubjectTypeListPage,
	'/administration/roles': RolesPage,
	'/administration/sso': SsoPage,
	'/administration/retention': RetentionPage,
	'/administration/ai-engines': AiEnginesPage,
	'/administration/account-ai-assignments': AccountAiAssignmentsPage,
	'/administration/organization-ai-assignments': OrganizationAiAssignmentsPage,
	'/administration/document-templates': OrganizationDocumentTemplatesPage,
	'/administration/ai-costs': AiCostsPage,
	'/administration/ai-cost-limits': AiCostLimitsPage,
	'/administration/audit-trail': AuditTrailPage,
};

/**
 * One entry per `FacilitySectionPath`, same compiler-checked exhaustiveness as `ADMINISTRATION_PAGES`:
 * the `Record` refuses a catalogue entry with no page, and vice versa.
 */
const FACILITY_SECTION_PAGES: Record<FacilitySectionPath, React.ComponentType> = {
	'/facilities': FacilitiesPage,
	'/facilities/staff-members': StaffMembersPage,
	'/facilities/ai-assignments': FacilityAiAssignmentsPage,
	'/facilities/document-templates': FacilityDocumentTemplatesPage,
};

/**
 * The union of every permission in `FACILITY_SECTIONS` that lets a caller enter the section at all,
 * written once rather than derived from the catalogue: it must stay exactly the permissions this
 * entry-point route accepts, not silently grow the day a further section is appended to the catalogue.
 * `AI_ASSIGNMENT_READ` and `DOCUMENT_TEMPLATE_READ` are added here as a deliberate, explicit decision,
 * not an oversight: without one of them, a caller holding only that permission would see its own tab in
 * the section and could reach its screen directly, yet be refused on `/facilities` itself — the exact
 * entry-point defect this union already exists to prevent for `FACILITY_READ`/`STAFF_READ`.
 */
const FACILITY_SECTION_ENTRY_PERMISSIONS = [
	PermissionEnum.FACILITY_READ,
	PermissionEnum.STAFF_READ,
	PermissionEnum.AI_ASSIGNMENT_READ,
	PermissionEnum.DOCUMENT_TEMPLATE_READ,
] as const;

const guardBeneficiaryRead: ReadGuard = (action, element) => (
	<RequireBeneficiaryAccess action={action}>{element}</RequireBeneficiaryAccess>
);

const passThroughRead: ReadGuard = (_action, element) => element;

const Application: React.FC = () => {
	return (
		// UiKitProvider only descends a config context (no app state, no dependency on the store):
		// it wraps everything else so any component in the tree can rely on it.
		<UiKitProvider>
			<MessageBrokerProvider>
				<StateManagerProvider stores={rootStore}>
					<NotificationProvider>
						{/* Registers the middleware that turns dispatched actions into toasts — without it,
						no action ever shows a notification. */}
						<NotificationInitializer />
						{/* Mounted above `AuthenticatedUserProvider`, which withholds its children behind a
						loading spinner until the session probe resolves: nesting the listener inside it would
						risk missing a `beforeinstallprompt` firing during that probe. */}
						<InstallPromptProvider>
							<AuthenticatedUserProvider>
								<PermissionProvider>
									<GlobalStyles />
									<PortsProvider>
										<ApplicationOverlays />
										<Routes>
											<Route
												element={
													<RootPage>
														<Outlet />
													</RootPage>
												}
											>
												<Route path="login" element={<LoginRootPage />}>
													<Route
														index
														element={
															<RequireAnonymous>
																<LoginPage />
															</RequireAnonymous>
														}
													/>
													<Route
														path="second-factor"
														element={
															<RequireChallenge purpose="verification">
																<SecondFactorPage />
															</RequireChallenge>
														}
													/>
													<Route
														path="enrollment"
														element={
															<RequireChallenge purpose="enrollment">
																<SecondFactorEnrollmentPage />
															</RequireChallenge>
														}
													/>
												</Route>
												<Route element={<LoginRootPage />}>
													<Route
														path="invitation/:token"
														element={
															<RequireAnonymous>
																<InvitationPage />
															</RequireAnonymous>
														}
													/>
													<Route path="password">
														<Route
															path="forgotten"
															element={
																<RequireAnonymous>
																	<PasswordForgottenPage />
																</RequireAnonymous>
															}
														/>
														<Route
															path="reset/:token"
															element={
																<RequireAnonymous>
																	<PasswordResetPage />
																</RequireAnonymous>
															}
														/>
													</Route>
												</Route>
												<Route
													element={
														<RequireSession>
															<SessionLossWatcher>
																<AppShell />
															</SessionLossWatcher>
														</RequireSession>
													}
												>
													{/* Outside `RequireFacility`: the home works without a working agency. */}
													<Route index element={<HomePage />} />
													<Route
														path="dashboard"
														element={<Navigate to="/" replace />}
													/>
													{/*
														Groups every route whose screen assumes a resolved, non-null current
														facility: `RequireFacility` renders an explicit loading/empty state in
														place of every route below's own element instead of each one nesting it
														individually. Nothing else is nested here — `/administration*` and
														`/facilities*` stay outside, on purpose.
													*/}
													<Route
														element={
															<RequireFacility>
																<Outlet />
															</RequireFacility>
														}
													>
														<Route path="subjects/beneficiary">
															<Route
																index
																element={
																	<RequireBeneficiaryAccess action="consulter les dossiers">
																		<BeneficiariesListPage />
																	</RequireBeneficiaryAccess>
																}
															/>
															<Route
																path=":beneficiary"
																element={<SubjectLayout />}
															>
																<Route
																	index
																	element={
																		<RequireBeneficiaryAccess action="consulter un dossier">
																			<BeneficiarySummaryPage />
																		</RequireBeneficiaryAccess>
																	}
																/>
																<Route
																	path="members"
																	element={
																		<RequireBeneficiaryAccess action="consulter les membres d'un dossier">
																			<BeneficiaryMembersPage />
																		</RequireBeneficiaryAccess>
																	}
																/>
																<Route
																	path="assignees"
																	element={
																		<Navigate to="../members" replace />
																	}
																/>
																<Route
																	path="contacts"
																	element={
																		<RequireBeneficiaryAccess action="consulter les contacts d'un dossier">
																			<BeneficiaryContactsPage />
																		</RequireBeneficiaryAccess>
																	}
																/>
																{renderSubjectDepositRoutes(
																	guardBeneficiaryRead,
																)}
															</Route>
														</Route>
														<Route
															path="subjects/:subjectType"
															element={
																<RequireSubjectType>
																	<Outlet />
																</RequireSubjectType>
															}
														>
															<Route index element={<SubjectListPage />} />
															<Route
																path=":subject"
																element={<SubjectLayout />}
															>
																<Route
																	index
																	element={<SubjectDetailsPage />}
																/>
																<Route
																	path="members"
																	element={<SubjectMembersPage />}
																/>
																{renderSubjectDepositRoutes(passThroughRead)}
															</Route>
														</Route>
														<Route
															path="workflow-runs"
															element={
																<RequirePermission
																	permission={
																		PermissionEnum.WORKFLOW_RUN_FACILITY_READ
																	}
																	action="suivre les traitements"
																>
																	<WorkflowRunsPage />
																</RequirePermission>
															}
														/>
													</Route>
													{/*
														The section's own paths stay absolute, straight off the catalogue:
														`SectionTabs` needs them absolute for its `NavLink`s, and React Router
														accepts an absolute child path that extends its parent's. Deriving a
														relative segment here would mean re-splitting a string the catalogue
														already holds whole.
													*/}
													<Route path="/administration">
														<Route
															index
															element={
																<SectionLanding
																	sections={ADMINISTRATION_SECTIONS}
																	emptyMessage="Vous n'avez accès à aucun écran d'administration."
																/>
															}
														/>
														{ADMINISTRATION_SECTIONS.map((section) => {
															const AdministrationPage =
																ADMINISTRATION_PAGES[section.path];

															return (
																<Route
																	key={section.path}
																	path={section.path}
																	element={
																		<RequirePermission
																			permission={section.permission}
																			action={section.action}
																		>
																			<AdministrationPage />
																		</RequirePermission>
																	}
																/>
															);
														})}
														<Route
															path="/administration/document-groups/new"
															element={
																<RequirePermission
																	permission={
																		PermissionEnum.ORGANIZATION_WRITE
																	}
																	action="gérer les groupes de documents"
																>
																	<DocumentGroupEditPage />
																</RequirePermission>
															}
														/>
														<Route
															path="/administration/document-groups/:workflowKey"
															element={
																<RequirePermission
																	permission={
																		PermissionEnum.ORGANIZATION_WRITE
																	}
																	action="gérer les groupes de documents"
																>
																	<DocumentGroupEditPage />
																</RequirePermission>
															}
														/>
														<Route
															path="/administration/subject-types/beneficiary"
															element={
																<RequirePermission
																	permission={
																		PermissionEnum.SUBJECT_TYPE_MANAGE
																	}
																	action="gérer l'accès aux dossiers des bénéficiaires"
																>
																	<BeneficiaryTypeAccessPage />
																</RequirePermission>
															}
														/>
														<Route
															path="/administration/subject-types/new"
															element={
																<RequirePermission
																	permission={
																		PermissionEnum.SUBJECT_TYPE_MANAGE
																	}
																	action="gérer les types de dossier"
																>
																	<SubjectTypeEditPage />
																</RequirePermission>
															}
														/>
														<Route
															path="/administration/subject-types/:subjectTypeId"
															element={
																<RequirePermission
																	permission={
																		PermissionEnum.SUBJECT_TYPE_MANAGE
																	}
																	action="gérer les types de dossier"
																>
																	<SubjectTypeEditPage />
																</RequirePermission>
															}
														/>
														<Route
															path="facilities"
															element={<Navigate to="/facilities" replace />}
														/>
													</Route>
													<Route path="/facilities">
														{/*
															Declared as the index, outside the `.map()` below: `/facilities` is the
															only entry of the catalogue whose route guard (the union of every entry
															permission in the section) differs from the permission of its own
															catalogue entry (`FACILITY_READ` alone, which still governs its tab in
															`SectionTabs`). Folding it back into the `.map()` would guard it on
															`FACILITY_READ` only, refusing access to a `STAFF_READ`-only or
															`AI_ASSIGNMENT_READ`-only caller — exactly the entry-point defect already
															fixed on the administration section.
														*/}
														<Route
															index
															element={
																<RequirePermission
																	permission={
																		FACILITY_SECTION_ENTRY_PERMISSIONS
																	}
																	mode="any"
																	action="consulter la section Agences"
																>
																	<FacilitySectionEntry />
																</RequirePermission>
															}
														/>
														{FACILITY_SECTIONS.filter(
															(section) => section.path !== '/facilities',
														).map((section) => {
															const FacilityPage =
																FACILITY_SECTION_PAGES[section.path];

															return (
																<Route
																	key={section.path}
																	path={section.path}
																	element={
																		<RequirePermission
																			permission={section.permission}
																			action={section.action}
																		>
																			<FacilityPage />
																		</RequirePermission>
																	}
																/>
															);
														})}
													</Route>
												</Route>
											</Route>
										</Routes>
									</PortsProvider>
								</PermissionProvider>
							</AuthenticatedUserProvider>
						</InstallPromptProvider>
					</NotificationProvider>
				</StateManagerProvider>
			</MessageBrokerProvider>
		</UiKitProvider>
	);
};

export default Application;
